import { useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  deletionBlocker, dueDateFor, earlierUninvoiced, entryDuration, entryEarnings, evenPercents, fixedItemsForInvoice, formatMoney, installmentLead, installmentPlanError,
  installmentsOf, installmentTotals, invoiceableEntries, invoiceDocument, invoiceRange, isLive, makeId, markInvoicePaid, MAX_INSTALLMENTS, nextInvoiceNumber,
  revertBlocker, revertInvoiceToDraft, sendChecks, sendInvoice, setInstallments, setInvoicePeriod, taskLabel, trashInvoice,
  type FixedLine, type Invoice, type InvoiceGrouping, type ManualLine, type PaymentTerm, type TaxLine
} from "@otrack/domain";
import { Button, Checkbox, DateField, EditableText, Field, Icon, IconButton, Menu, Modal, Segmented, Select, TextInput, type MenuItem } from "@otrack/ui";
import { Chip, EmptyState, money, MoneyField, PageHeader, Panel, rise } from "../components";
import { useApp, useDataset, useFactory } from "../context";
import { invoiceState, InvoiceStateTag, parsePercent, percentText, periodLabel, TERMS } from "../invoice-list";
import { InvoicePaper } from "../invoice-paper";
import { href, navigate } from "../router";
import { formatDuration, formatShortDate, startOfDay } from "../time";

export function InvoicePage({ id }: { id: string }) {
  const { now, act, notify } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const [dialog, setDialog] = useState<"send" | "paid" | "split" | null>(null);
  const invoice = dataset.invoices[id];
  if (!invoice || !isLive(invoice)) return <div className="ot-page">
    <PageHeader title="Invoice not found" back={{ label: "Invoices", href: href({ page: "invoices" }) }}/>
    <EmptyState icon="receipt" title="This invoice is gone">It may have been moved to Trash or never synced to this device.</EmptyState>
  </div>;

  const client = dataset.clients[invoice.clientId];
  const doc = invoiceDocument(dataset, invoice);
  const state = invoiceState(invoice, now);
  const title = invoice.number ?? "Draft invoice";
  const lead = installmentLead(dataset, invoice);
  const split = doc.installment !== null;
  const splitItem: MenuItem[] = split
    ? !installmentsOf(dataset, invoice).some((member) => member.status === "draft") ? [] : [{ label: "Change installments", icon: "pie-chart", onSelect: () => setDialog("split") }]
    : invoice.status === "draft" && !invoice.number ? [{ label: "Split into installments", icon: "pie-chart", hint: "Charge it in parts", onSelect: () => setDialog("split") }] : [];

  const exportPdf = () => {
    // The file name a browser suggests for "Save as PDF" comes from the page title.
    const previous = window.document.title;
    window.document.title = `${invoice.number ?? "Draft"} ${client?.name ?? ""}`.trim();
    const restore = () => { window.document.title = previous; window.removeEventListener("afterprint", restore); };
    window.addEventListener("afterprint", restore);
    window.print();
  };
  const trash = async () => {
    const blocker = deletionBlocker(dataset, "invoice", id);
    if (blocker) return notify(blocker);
    await act(trashInvoice(dataset, invoice, Date.now(), factory), split ? "Draft and its installments moved to Trash. Its time is uninvoiced again" : "Draft moved to Trash. Its time is uninvoiced again");
    navigate({ page: "invoices" });
  };

  return <div className="ot-page">
    <PageHeader
      back={{ label: "Invoices", href: href({ page: "invoices" }) }}
      title={<span className={invoice.number ? "ot-mono ot-invoice__title" : undefined}>{title}</span>}
      subtitle={<div className="ot-chips">
        {client ? <Chip icon="briefcase" href={href({ page: "client", id: client.id })}>{client.name}</Chip> : <Chip icon="briefcase" tone="muted">Unknown client</Chip>}
        <InvoiceStateTag state={state}/>
        {doc.installment && <Chip icon="pie-chart">Installment {doc.installment.index} of {doc.installment.count}, {percentText(doc.installment.percent)}%</Chip>}
        <Chip icon="coins">{money(doc.totals.total, invoice.currency)}</Chip>
      </div>}
      actions={<>
        <Button icon="printer" onClick={exportPdf}>Export PDF</Button>
        {invoice.status === "draft" && <Button tone="primary" icon="send" onClick={() => setDialog("send")}>Mark as sent</Button>}
        {invoice.status === "sent" && <Button tone="primary" icon="hand-coins" onClick={() => setDialog("paid")}>Mark as paid</Button>}
        {invoice.status === "draft" && <Menu label="Invoice actions" items={[...splitItem, { label: "Move to Trash", icon: "trash", tone: "danger", onSelect: () => void trash() }]}/>}
        {invoice.status === "sent" && <Menu label="Invoice actions" items={[...splitItem, {
          label: "Back to draft", icon: "undo", hint: lead.id === invoice.id ? "Unlocks its entries" : "Its share can change again",
          onSelect: () => {
            const blocker = revertBlocker(dataset, invoice);
            if (blocker) return notify(blocker);
            void act(revertInvoiceToDraft(invoice, factory), lead.id === invoice.id ? `${title} is a draft again. Its entries are unlocked` : `${title} is a draft again`);
          }
        }]}/>}
        {invoice.status === "paid" && split && <Menu label="Invoice actions" items={splitItem}/>}
      </>}
    />
    <div className="ot-invoice">
      <motion.div className="ot-invoice__sheet" {...rise(1)}>
        <InvoicePaper invoice={invoice} document={doc}/>
      </motion.div>
      <aside className="ot-invoice__side ot-noprint">
        {split && <InstallmentsPanel invoice={invoice} onChange={() => setDialog("split")}/>}
        {invoice.status === "draft" ? <DraftEditor invoice={invoice} lead={lead}/> : <SentStatus invoice={invoice} lead={lead}/>}
      </aside>
    </div>
    <AnimatePresence>
      {dialog === "send" && <SendDialog key="send" invoice={invoice} onClose={() => setDialog(null)}/>}
      {dialog === "paid" && <PaidDialog key="paid" invoice={invoice} onClose={() => setDialog(null)}/>}
      {dialog === "split" && <InstallmentsDialog key="split" invoice={invoice} onClose={() => setDialog(null)}/>}
    </AnimatePresence>
  </div>;
}

function useSaveInvoice(invoice: Invoice) {
  const { act } = useApp();
  const factory = useFactory();
  return (patch: Partial<Invoice>, message: string) => act([factory.op("invoice", invoice.id, patch)], message);
}

/* ---------------------------------------------------------------- draft editor */

/** Installments share Installment 1's lines, so those panels edit it; once it is sent they are frozen. */
function DraftEditor({ invoice, lead }: { invoice: Invoice; lead: Invoice }) {
  if (lead.status !== "draft") return <DetailsPanel invoice={invoice} lead={lead}/>;
  return <>
    <PeriodPanel invoice={lead}/>
    <TimePanel invoice={lead}/>
    <FixedPanel invoice={lead}/>
    <LinesPanel invoice={lead}/>
    <TaxPanel invoice={lead}/>
    <DetailsPanel invoice={invoice} lead={lead}/>
  </>;
}

function PeriodPanel({ invoice }: { invoice: Invoice }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const earlier = earlierUninvoiced(dataset, invoice);
  const move = (from: number, to: number, message: string) => act(setInvoicePeriod(dataset, invoice, from, to, factory), message);

  return <Panel title="Billing period" icon="calendar" description="Changing it picks up all uninvoiced work in the new period." index={2}>
    <div className="ot-form__pair">
      <Field label="From"><DateField value={invoice.periodFrom} onChange={(day) => void move(day, invoice.periodTo, "Period updated")}/></Field>
      <Field label="To"><DateField value={invoice.periodTo} onChange={(day) => void move(invoice.periodFrom, day, "Period updated")}/></Field>
    </div>
    <AnimatePresence initial={false}>
      {earlier.entries.length > 0 && <motion.div key="earlier" className="ot-earlier" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}>
        <p>
          <strong>{earlier.entries.length} earlier uninvoiced {earlier.entries.length === 1 ? "entry" : "entries"}</strong>
          <span className="ot-mono">{formatDuration(earlier.ms)}, {money(earlier.amount, invoice.currency)}</span>
        </p>
        <Button tone="ghost" icon="plus" onClick={() => void move(earlier.entries[0]!.start, invoice.periodTo, `Included ${earlier.entries.length} earlier ${earlier.entries.length === 1 ? "entry" : "entries"}`)}>Include them</Button>
      </motion.div>}
    </AnimatePresence>
  </Panel>;
}

const GROUPING: Array<{ value: InvoiceGrouping; label: string }> = [{ value: "entry", label: "Entry" }, { value: "task", label: "Task" }, { value: "project", label: "Project" }];
const entryDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

function TimePanel({ invoice }: { invoice: Invoice }) {
  const { now, act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const save = useSaveInvoice(invoice);
  const entries = invoiceableEntries(dataset, invoice.clientId, invoice.currency, invoiceRange(invoice), invoice.id);
  const included = entries.filter((entry) => entry.invoiceId === invoice.id);
  const rounding = dataset.clients[invoice.clientId]?.roundingMinutes ?? null;
  const toggle = (ids: string[], on: boolean, message: string) => act(ids.map((entryId) => factory.op("timeEntry", entryId, { invoiceId: on ? invoice.id : null })), message);

  return <Panel
    title="Time"
    icon="clock"
    description={rounding ? `Each entry rounds up to ${rounding} minutes on the invoice.` : "Billed at the rate each entry was recorded with."}
    index={3}
  >
    <Field label="Group lines by">
      <Segmented stretch size="sm" value={invoice.grouping} options={GROUPING} onChange={(grouping) => void save({ grouping }, `Grouped by ${grouping}`)}/>
    </Field>
    {entries.length === 0 ? <p className="ot-empty">No uninvoiced billable time in this period.</p> : <>
      <div className="ot-ientries__head">
        <span><strong className="ot-mono">{included.length}</strong> of {entries.length} included</span>
        {included.length < entries.length
          ? <button type="button" className="ot-link-button" onClick={() => void toggle(entries.filter((entry) => entry.invoiceId !== invoice.id).map((entry) => entry.id), true, "Included all time")}>Include all</button>
          : <button type="button" className="ot-link-button" onClick={() => void toggle(included.map((entry) => entry.id), false, "Removed all time")}>Remove all</button>}
      </div>
      <div className="ot-ientries">
        {entries.map((entry) => {
          const on = entry.invoiceId === invoice.id;
          const project = entry.projectId ? dataset.projects[entry.projectId] : undefined;
          const task = entry.taskId ? dataset.tasks[entry.taskId] : undefined;
          const earned = entryEarnings(entry, now);
          return <label key={entry.id} className={`ot-ientry ${on ? "is-on" : ""}`}>
            <Checkbox checked={on} aria-label={`Bill ${entry.description || "this entry"}`} onChange={(next) => void toggle([entry.id], next, next ? "Entry included" : "Entry removed")}/>
            <span className="ot-ientry__date ot-mono">{entryDay.format(entry.start)}</span>
            <span className="ot-ientry__main">
              <span>{entry.description || (task && taskLabel(dataset, task)) || <em>No description</em>}</span>
              <small>{[project?.name, entry.description && task ? taskLabel(dataset, task) : null].filter(Boolean).join(", ")}</small>
            </span>
            <span className="ot-ientry__figures ot-mono">
              <span>{formatDuration(entryDuration(entry, now))}</span>
              {earned && <small>{formatMoney(earned)}</small>}
            </span>
          </label>;
        })}
      </div>
    </>}
  </Panel>;
}

function FixedPanel({ invoice }: { invoice: Invoice }) {
  const save = useSaveInvoice(invoice);
  const dataset = useDataset();
  const items = fixedItemsForInvoice(dataset, invoice);
  if (!items.length) return null;
  const setLines = (fixedLines: FixedLine[], message: string) => save({ fixedLines }, message);

  return <Panel title="Fixed prices" icon="coins" description="Finished items bill their balance. Bill a deposit on anything still in progress." index={4}>
    <div className="ot-fixed">
      {items.map(({ item, elsewhere, lines, remaining, depositRoom, canAddBalance }) => {
        const key = `${item.ref.type}:${item.ref.id}`;
        const balance = lines.find((line) => line.kind === "balance");
        const deposits = lines.filter((line) => line.kind === "deposit");
        const status = [item.earnedAt !== null ? "Done" : "In progress", elsewhere > 0 ? `${money(elsewhere, item.currency)} on other invoices` : null].filter(Boolean).join(", ");
        return <div className="ot-fixed__item" key={key}>
          <div className="ot-fixed__head">
            <span className="ot-fixed__name">
              <strong>{item.name}</strong>
              <small>{item.task ? item.project.name : "Whole project"}, {status.toLowerCase()}</small>
            </span>
            <span className="ot-mono ot-fixed__price">{money(item.price, item.currency)}</span>
          </div>
          {deposits.map((line) => <div className="ot-fixed__line" key={line.id}>
            <Icon name="percent"/>
            <span>Deposit</span>
            <EditableText
              size="sm"
              className="ot-fixed__percent"
              value={percentText(line.percent)}
              inputMode="decimal"
              aria-label={`Deposit percentage for ${item.name}`}
              trailing={<span className="ot-unit">%</span>}
              onCommit={async (text) => {
                const percent = parsePercent(text);
                if (!percent || percent > depositRoom + line.percent) return `Between 0 and ${percentText(depositRoom + line.percent)}%.`;
                if (percent !== line.percent) await setLines(invoice.fixedLines.map((other) => (other.id === line.id ? { ...other, percent } : other)), `Deposit set to ${percentText(percent)}%`);
                return null;
              }}
            />
            <IconButton className="is-ghost" icon="close" label="Remove deposit" tooltip={false} onClick={() => void setLines(invoice.fixedLines.filter((other) => other.id !== line.id), "Deposit removed")}/>
          </div>)}
          {balance && <div className="ot-fixed__line">
            <Icon name="check"/>
            <span>{elsewhere > 0 ? "Balance included" : "Included in full"}</span>
            <IconButton className="is-ghost" icon="close" label={`Remove ${item.name}`} tooltip={false} onClick={() => void setLines(invoice.fixedLines.filter((other) => other.id !== balance.id), `${item.name} removed`)}/>
          </div>}
          {!balance && remaining > 0 && <div className="ot-fixed__actions">
            {canAddBalance && <Button tone="ghost" icon="plus" onClick={() => void setLines([...invoice.fixedLines, { id: makeId("line"), item: item.ref, kind: "balance", percent: 0 }], `${item.name} added`)}>
              {elsewhere > 0 || deposits.length ? `Add balance, ${money(remaining, item.currency)}` : "Add to invoice"}
            </Button>}
            {depositRoom > 0 && <Button tone="ghost" icon="percent" onClick={() => void setLines([...invoice.fixedLines, { id: makeId("line"), item: item.ref, kind: "deposit", percent: Math.min(5000, depositRoom) }], `Deposit for ${item.name} added`)}>Add deposit</Button>}
          </div>}
        </div>;
      })}
    </div>
  </Panel>;
}

function LinesPanel({ invoice }: { invoice: Invoice }) {
  const save = useSaveInvoice(invoice);
  const [text, setText] = useState("");
  const setLines = (manualLines: ManualLine[], message: string) => save({ manualLines }, message);
  const update = (line: ManualLine, patch: Partial<ManualLine>, message: string) => setLines(invoice.manualLines.map((other) => (other.id === line.id ? { ...other, ...patch } : other)), message);
  const add = async (event: FormEvent) => {
    event.preventDefault();
    const description = text.trim();
    if (!description) return;
    setText("");
    await setLines([...invoice.manualLines, { id: makeId("line"), description, quantity: 1, unitPrice: 0 }], `Added ${description}`);
  };

  return <Panel title="Other lines" icon="plus" description="Expenses, licenses, anything not tracked as time." index={5}>
    {invoice.manualLines.length > 0 && <div className="ot-mlines">
      <AnimatePresence initial={false}>
        {invoice.manualLines.map((line) => <motion.div key={line.id} className="ot-mline" layout="position" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }}>
          <EditableText variant="ghost" size="sm" className="ot-mline__desc" value={line.description} maxLength={500} aria-label="Line description" onCommit={async (next) => {
            const description = next.trim();
            if (!description) return "Describe the line.";
            if (description !== line.description) await update(line, { description }, "Line renamed");
            return null;
          }}/>
          <div className="ot-mline__figures">
            <EditableText size="sm" className="ot-mline__qty" value={String(line.quantity)} inputMode="decimal" aria-label="Quantity" trailing={<span className="ot-unit">qty</span>} onCommit={async (next) => {
              const quantity = Number(next.trim().replace(",", "."));
              if (!Number.isFinite(quantity) || quantity < 0) return "A number, like 1 or 2.5";
              if (quantity !== line.quantity) await update(line, { quantity }, "Quantity updated");
              return null;
            }}/>
            <MoneyField size="sm" amount={line.unitPrice} currency={invoice.currency} aria-label="Unit price" onCommit={(unitPrice) => update(line, { unitPrice: unitPrice ?? 0 }, "Price updated")}/>
            <IconButton className="is-ghost" icon="close" label={`Remove ${line.description}`} tooltip={false} onClick={() => void setLines(invoice.manualLines.filter((other) => other.id !== line.id), "Line removed")}/>
          </div>
        </motion.div>)}
      </AnimatePresence>
    </div>}
    <form onSubmit={(event) => void add(event)}>
      <TextInput leading={<Icon name="plus"/>} value={text} onChange={(event) => setText(event.target.value)} placeholder="Describe a line and press Enter" aria-label="New line description" maxLength={500}/>
    </form>
  </Panel>;
}

type DiscountMode = "none" | "percent" | "amount";

function TaxPanel({ invoice }: { invoice: Invoice }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const save = useSaveInvoice(invoice);
  const client = dataset.clients[invoice.clientId];
  const mode: DiscountMode = invoice.discount?.type ?? "none";
  const setTaxes = (taxes: TaxLine[], message: string) => save({ taxes }, message);
  const updateTax = (tax: TaxLine, patch: Partial<TaxLine>, message: string) => setTaxes(invoice.taxes.map((other) => (other.id === tax.id ? { ...other, ...patch } : other)), message);
  const sameAsDefaults = client !== undefined && client.defaultTaxes.length === invoice.taxes.length && client.defaultTaxes.every((tax, index) => tax.name === invoice.taxes[index]?.name && tax.rate === invoice.taxes[index]?.rate);

  return <Panel title="Discount and tax" icon="percent" description="The discount comes off first. Each tax is a share of what is left." index={6}>
    <Field label="Discount">
      <Segmented
        stretch
        size="sm"
        value={mode}
        options={[{ value: "none", label: "None" }, { value: "percent", label: "Percent" }, { value: "amount", label: "Amount" }]}
        onChange={(next) => void save({ discount: next === "none" ? null : { type: next, value: 0 } }, next === "none" ? "Discount removed" : "Discount added")}
      />
    </Field>
    {invoice.discount?.type === "percent" && <EditableText value={percentText(invoice.discount.value)} inputMode="decimal" aria-label="Discount percentage" trailing={<span className="ot-unit">%</span>} onCommit={async (text) => {
      const value = parsePercent(text);
      if (value === undefined) return "A percentage from 0 to 100.";
      await save({ discount: { type: "percent", value } }, `Discount set to ${percentText(value)}%`);
      return null;
    }}/>}
    {invoice.discount?.type === "amount" && <MoneyField amount={invoice.discount.value} currency={invoice.currency} aria-label="Discount amount" onCommit={(value) => save({ discount: { type: "amount", value: value ?? 0 } }, "Discount updated")}/>}
    <div className="ot-taxes">
      <span className="ot-field__label">Taxes</span>
      {invoice.taxes.length === 0 && <p className="ot-empty ot-taxes__none">No tax on this invoice.</p>}
      {invoice.taxes.map((tax) => <div className="ot-tax" key={tax.id}>
        <EditableText size="sm" value={tax.name} placeholder="IVA" maxLength={60} aria-label="Tax name" onCommit={(text) => { if (text.trim() !== tax.name) void updateTax(tax, { name: text.trim() }, "Tax renamed"); }}/>
        <EditableText size="sm" className="ot-tax__rate" value={percentText(tax.rate)} inputMode="decimal" aria-label={`${tax.name || "Tax"} rate`} trailing={<span className="ot-unit">%</span>} onCommit={async (text) => {
          const rate = parsePercent(text);
          if (rate === undefined) return "0 to 100";
          if (rate !== tax.rate) await updateTax(tax, { rate }, `${tax.name || "Tax"} set to ${percentText(rate)}%`);
          return null;
        }}/>
        <IconButton className="is-ghost" icon="close" label={`Remove ${tax.name || "tax"}`} tooltip={false} onClick={() => void setTaxes(invoice.taxes.filter((other) => other.id !== tax.id), "Tax removed")}/>
      </div>)}
      <div className="ot-taxes__actions">
        <Button tone="ghost" icon="plus" disabled={invoice.taxes.length >= 10} onClick={() => void setTaxes([...invoice.taxes, { id: makeId("tax"), name: "", rate: 0 }], "Tax added")}>Add tax</Button>
        {client && !sameAsDefaults && <button type="button" className="ot-link-button" onClick={() => void act([factory.op("client", client.id, { defaultTaxes: invoice.taxes.map((tax) => ({ ...tax, id: makeId("tax") })) })], `New invoices for ${client.name} start with these taxes`)}>Use for {client.name}'s next invoices</button>}
      </div>
    </div>
  </Panel>;
}

function DetailsPanel({ invoice, lead }: { invoice: Invoice; lead: Invoice }) {
  const save = useSaveInvoice(invoice);
  const saveShared = useSaveInvoice(lead);
  const split = invoice.installment !== null;
  return <Panel title="Details" icon="pencil" description={split ? "For this installment. Changes save as you go." : "Changes save as you go."} index={7}>
    <div className="ot-stack">
      <Field label="Payment terms" hint="The due date is set from the issue date when you mark it sent.">
        <Select value={String(invoice.paymentTermDays)} options={TERMS} onChange={(value) => void save({ paymentTermDays: Number(value) as PaymentTerm }, `Terms set to ${TERMS.find((term) => term.value === value)?.label ?? value}`)}/>
      </Field>
      {lead.status === "draft" && <Field label="Reference" optional hint={split ? "A PO or contract number, printed on every installment." : "A PO or contract number, printed under the invoice number."}>
        <EditableText value={lead.reference} maxLength={100} placeholder="PO-4471" onCommit={(text) => { if (text.trim() !== lead.reference) void saveShared({ reference: text.trim() }, "Reference saved"); }}/>
      </Field>}
      <Field label="Note" optional hint="Printed above the payment instructions.">
        <EditableText value={invoice.note} multiline rows={3} maxLength={2000} placeholder="Thanks for a great quarter." onCommit={(text) => { if (text.trim() !== invoice.note) void save({ note: text.trim() }, "Note saved"); }}/>
      </Field>
    </div>
  </Panel>;
}

/* ---------------------------------------------------------------- sent and paid */

function SentStatus({ invoice, lead }: { invoice: Invoice; lead: Invoice }) {
  const { now } = useApp();
  const dataset = useDataset();
  const state = invoiceState(invoice, now);
  const entries = Object.values(dataset.timeEntries).filter((entry) => isLive(entry) && entry.invoiceId === lead.id).length;
  const daysLate = invoice.dueDate !== null ? Math.round((startOfDay(now) - invoice.dueDate) / 86_400_000) : 0;

  return <Panel title={state === "paid" ? "Paid" : "Waiting for payment"} icon={state === "paid" ? "seal-check" : "send"} className="ot-panel--sticky" index={2}>
    <ol className="ot-timeline">
      <li className="is-done"><span className="ot-timeline__dot" aria-hidden="true"/><span>Issued</span><strong className="ot-mono">{invoice.issueDate !== null ? formatShortDate(invoice.issueDate) : ""}</strong></li>
      <li className={state === "overdue" ? "is-late" : "is-done"}>
        <span className="ot-timeline__dot" aria-hidden="true"/>
        <span>{state === "overdue" ? `Due, ${daysLate} ${daysLate === 1 ? "day" : "days"} late` : "Due"}</span>
        <strong className="ot-mono">{invoice.dueDate !== null ? formatShortDate(invoice.dueDate) : ""}</strong>
      </li>
      <li className={state === "paid" ? "is-done is-final" : ""}>
        <span className="ot-timeline__dot" aria-hidden="true"/>
        <span>Paid</span>
        <strong className="ot-mono">{invoice.paidAt !== null ? formatShortDate(invoice.paidAt) : "Not yet"}</strong>
      </li>
    </ol>
    <dl className="ot-ifacts">
      <div><dt>Period</dt><dd>{periodLabel(invoice)}</dd></div>
      <div><dt>Entries</dt><dd className="ot-mono">{entries}</dd></div>
      <div><dt>Terms</dt><dd>{TERMS.find((term) => term.value === String(invoice.paymentTermDays))?.label}</dd></div>
    </dl>
    <p className="ot-note"><Icon name="info"/>{state === "paid"
      ? "Paid invoices are final. Undo right after marking one paid if it was a slip."
      : "Its lines and entries are locked. Move it back to draft from the menu to change anything; it keeps its number."}</p>
  </Panel>;
}

function SendDialog({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { now, act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const [issue, setIssue] = useState(invoice.issueDate ?? startOfDay(now));
  const checks = sendChecks(dataset, invoice);
  const number = invoice.number ?? nextInvoiceNumber(dataset, new Date(issue).getFullYear()).number;
  const client = dataset.clients[invoice.clientId];
  const { totals, installment } = invoiceDocument(dataset, invoice);
  const description = !installment
    ? "Its lines freeze as they are now and its entries lock. Export the PDF to send it to your client."
    : installment.index === 1
      ? `Its entries lock and the lines freeze for all ${installment.count} installments. Export the PDF to send it to your client.`
      : `It bills ${percentText(installment.percent)}% of the lines frozen with installment 1. Export the PDF to send it to your client.`;

  const send = async () => {
    await act(sendInvoice(dataset, invoice, issue, factory), `${number} marked as sent`);
    onClose();
  };

  return <Modal title={`Send ${number}`} icon="send" description={description} onClose={onClose}>
    <div className="ot-form">
      <div className="ot-form__pair">
        <Field label="Issue date"><DateField value={issue} onChange={setIssue}/></Field>
        <Field label="Due date"><div className="ot-readonly ot-mono">{formatShortDate(dueDateFor(issue, invoice.paymentTermDays))}</div></Field>
      </div>
      <dl className="ot-summary-grid ot-summary-grid--3">
        <div><dt>Number</dt><dd className="ot-mono">{number}</dd></div>
        <div><dt>Client</dt><dd>{client?.name}</dd></div>
        <div><dt>Total</dt><dd className="ot-mono">{money(totals.total, invoice.currency)}</dd></div>
      </dl>
      {(checks.blockers.length > 0 || checks.warnings.length > 0) && <ul className="ot-checks">
        {checks.blockers.map((message) => <li key={message} className="is-blocker"><Icon name="warning" weight="bold"/><span>{message}</span>{message.includes("business name") && <a href={href({ page: "settings" })}>Open Settings</a>}</li>)}
        {checks.warnings.map((warning) => <li key={warning.message}><Icon name="info"/><span>{warning.message}</span>
          <a href={warning.fix === "profile" ? href({ page: "settings" }) : href({ page: "client", id: invoice.clientId })}>{warning.fix === "profile" ? "Settings" : "Client"}</a>
        </li>)}
      </ul>}
      <div className="ot-form__actions">
        <span className="ot-form__spacer"/>
        <Button onClick={onClose}>Cancel</Button>
        <Button tone="primary" icon="send" disabled={checks.blockers.length > 0} onClick={() => void send()}>Mark as sent</Button>
      </div>
    </div>
  </Modal>;
}

function PaidDialog({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { now, act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const [paid, setPaid] = useState(startOfDay(now));
  const { totals } = invoiceDocument(dataset, invoice);
  const pay = async () => {
    await act(markInvoicePaid(invoice, paid, factory), `${invoice.number ?? "Invoice"} paid. ${money(totals.total, invoice.currency)} received`);
    onClose();
  };
  return <Modal title={`${invoice.number ?? "Invoice"} paid`} icon="hand-coins" description={`${money(totals.total, invoice.currency)} from ${dataset.clients[invoice.clientId]?.name ?? "the client"}. The full amount; partial payments are not tracked.`} onClose={onClose}>
    <div className="ot-form">
      <Field label="Paid on"><DateField value={paid} onChange={setPaid} max={now}/></Field>
      <div className="ot-form__actions">
        <span className="ot-form__spacer"/>
        <Button onClick={onClose}>Cancel</Button>
        <Button tone="primary" icon="check" onClick={() => void pay()}>Mark as paid</Button>
      </div>
    </div>
  </Modal>;
}

/* ---------------------------------------------------------------- installments */

function InstallmentsPanel({ invoice, onChange }: { invoice: Invoice; onChange: () => void }) {
  const { now } = useApp();
  const dataset = useDataset();
  const group = installmentsOf(dataset, invoice);
  const frozen = group[0]!.status !== "draft";
  return <Panel
    title="Installments"
    icon="pie-chart"
    description={frozen ? "Installment 1 is sent, so the lines are frozen for all of them. Send the rest in order." : "They share these lines, discount, and tax. Each has its own number, dates, and terms."}
    aside={group.some((member) => member.status === "draft") ? <button type="button" className="ot-link-button" onClick={onChange}>Change</button> : undefined}
    index={1}
  >
    <div className="ot-installments">
      {group.map((member, position) => {
        const current = member.id === invoice.id;
        return <a key={member.id} className={`ot-installment ${current ? "is-current" : ""}`} href={href({ page: "invoice", id: member.id })} aria-current={current ? "page" : undefined}>
          <span className="ot-installment__main">
            <strong>Installment {position + 1}</strong>
            <small>{percentText(member.installment!.percent)}%{member.number && <>, <span className="ot-mono">{member.number}</span></>}</small>
          </span>
          <InvoiceStateTag state={invoiceState(member, now)}/>
          <span className="ot-installment__amount ot-mono">{money(invoiceDocument(dataset, member).totals.total, member.currency)}</span>
        </a>;
      })}
    </div>
  </Panel>;
}

/**
 * Splits a Draft into 2 to 5 Installments, or changes the shares of a split. Sent Installments keep
 * their share; numbered ones can't be removed, so the numbering never has gaps.
 */
function InstallmentsDialog({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const lead = installmentLead(dataset, invoice);
  const group = installmentsOf(dataset, lead);
  const members = group.length ? group : [lead];
  const [texts, setTexts] = useState(() => (group.length ? group.map((member) => percentText(member.installment!.percent)) : evenPercents(2).map(percentText)));
  const { whole } = invoiceDocument(dataset, lead);
  const sent = (position: number) => (members[position]?.status ?? "draft") !== "draft";
  const fewest = Math.max(2, ...members.map((member, position) => (member.number ? position + 1 : 0)));
  const percents = texts.map((text) => parsePercent(text) ?? -1);
  const sum = percents.reduce((total, percent) => total + Math.max(0, percent), 0);
  const error = installmentPlanError(dataset, lead, percents);
  const canMerge = group.length > 0 && installmentPlanError(dataset, lead, [10_000]) === null;

  // A new count spreads what the sent Installments leave evenly over the others.
  const resize = (count: number) => {
    const open = Array.from({ length: count }, (_, position) => position).filter((position) => !sent(position));
    const left = 10_000 - percents.slice(0, count).reduce((sum, percent, position) => sum + (sent(position) ? percent : 0), 0);
    const spread = open.length && left >= open.length * 100 ? evenPercents(open.length, left) : [];
    setTexts(Array.from({ length: count }, (_, position) => (sent(position) ? texts[position]! : percentText(spread[open.indexOf(position)] ?? 0))));
  };
  const save = async (next: number[]) => {
    const message = next.length === 1
      ? "Installments merged into one invoice"
      : `${group.length ? "Installments set to" : `Split into ${next.length} installments:`} ${next.map((percent) => `${percentText(percent)}%`).join(", ")}`;
    await act(setInstallments(dataset, lead, next, Date.now(), factory), message);
    onClose();
    // The page's own Installment may be gone; Installment 1 always stays.
    if (members.findIndex((member) => member.id === invoice.id) >= next.length) navigate({ page: "invoice", id: lead.id });
  };

  return <Modal
    title={group.length ? "Change installments" : "Split into installments"}
    icon="pie-chart"
    description="Bill the client in parts. Each installment is its own invoice, with its own number and due date."
    onClose={onClose}
  >
    <div className="ot-form">
      <Field label="Installments">
        <Segmented
          stretch
          size="sm"
          value={String(texts.length)}
          options={Array.from({ length: MAX_INSTALLMENTS - fewest + 1 }, (_, offset) => ({ value: String(fewest + offset), label: String(fewest + offset) }))}
          onChange={(value) => resize(Number(value))}
        />
      </Field>
      <div className="ot-shares" role="group" aria-label="Share of the invoice">
        {texts.map((text, position) => {
          const member = members[position];
          const percent = percents[position]!;
          // While the shares don't add up, each shows its plain share so the amounts don't jump around.
          const amount = !error ? installmentTotals(whole, percents, position).total : percent >= 0 ? Math.round(whole.total * percent / 10_000) : null;
          return <div className={`ot-shares__row ${sent(position) ? "is-locked" : ""}`} key={position}>
            <span className="ot-shares__index ot-mono" aria-hidden="true">{position + 1}</span>
            <span className="ot-shares__label">
              <strong>Installment {position + 1}</strong>
              <small>{member ? (sent(position) ? `${member.number}, ${member.status}` : member.number ?? "Draft") : "New"}</small>
            </span>
            <TextInput
              size="sm"
              className="ot-shares__percent"
              value={text}
              inputMode="numeric"
              disabled={sent(position)}
              aria-label={`Installment ${position + 1} percentage`}
              trailing={<span className="ot-unit">%</span>}
              onChange={(event) => setTexts(texts.map((other, index) => (index === position ? event.target.value : other)))}
            />
            <span className="ot-shares__amount ot-mono">{amount === null ? "" : money(amount, lead.currency)}</span>
          </div>;
        })}
        <div className={`ot-shares__total ${sum === 10_000 ? "" : "is-off"}`}>
          <span>Total</span>
          <span className="ot-mono">{percentText(sum)}%</span>
          <span className="ot-mono">{money(whole.total, lead.currency)}</span>
        </div>
      </div>
      {error && <ul className="ot-checks"><li className="is-blocker"><Icon name="warning" weight="bold"/><span>{error}</span></li></ul>}
      <div className="ot-form__actions">
        {canMerge && <Button tone="ghost" icon="undo" onClick={() => void save([10_000])}>Merge into one invoice</Button>}
        <span className="ot-form__spacer"/>
        <Button onClick={onClose}>Cancel</Button>
        <Button tone="primary" icon="pie-chart" disabled={error !== null} onClick={() => void save(percents)}>{group.length ? "Save" : "Split invoice"}</Button>
      </div>
    </div>
  </Modal>;
}
